// Generic admin table: rows from a server resource, actions declared in the schema. Forms are
// generated from each action's declared input fields; the server executes and authorises.
import { useEffect, useRef, useState } from 'react';
import { Table, resolveColumns } from './DataTable';
import { Field, validateFields, visibleFields, visibleValues, type FieldDef } from '../../ui/Fields';
import { Modal } from '../../ui/Modal';
import { useToast } from '../../ui/Toasts';
import { Icon } from '../../ui/Icon';
import { errorMessage } from '../../app/api';
import { formatValue } from '../../lib/format';
import type { RegistryEntry, WidgetComponentProps } from '../types';

interface ActionDef {
  id: string;
  label: string;
  scope?: 'row' | 'table';
  confirm?: string;
  when?: Record<string, unknown>;
  input?: { type: 'json'; field: string } | { fields: FieldDef[] };
  opens?: string;
  /** Always shown as its own button (e.g. move up / down), with `icon` when given. */
  inline?: boolean;
  icon?: string;
  danger?: boolean;
}

function applies(a: ActionDef, row: Record<string, unknown>) {
  if (!a.when) return true;
  return Object.entries(a.when).every(([k, v]) => (Array.isArray(v) ? v.includes(row[k]) : row[k] === v));
}

/** Extra row actions behind a "More" menu so wide tables keep one clear primary action per row. */
function RowMenu({ actions, onPick }: { actions: ActionDef[]; onPick: (a: ActionDef) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="popover-anchor" ref={ref} style={{ display: 'inline-block' }}>
      <button className="icon-btn" aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="more" size={15} />
      </button>
      {open && (
        <div className="popover right" role="menu" style={{ minWidth: 200 }}>
          {actions.map((a) => (
            <button
              key={`${a.id}:${a.label}`}
              className={`option${a.danger ? ' danger' : ''}`}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick(a);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CrudTable({ data, props, ctx, widget }: WidgetComponentProps) {
  const toast = useToast();
  const actions: ActionDef[] = props.actions ?? [];
  const [open, setOpen] = useState<{ action: ActionDef; row: Record<string, any> | null } | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<{ row: any; items: any[] } | null>(null);

  const start = async (action: ActionDef, row: Record<string, any> | null) => {
    setError(null);
    if (action.opens === 'history') {
      try {
        const res = await ctx.action(action.id, row?.id ?? null, {});
        setHistory({ row, items: res.history ?? [] });
      } catch (e) {
        toast(errorMessage(e), 'error');
      }
      return;
    }
    const init: Record<string, any> = {};
    if (action.input && 'fields' in action.input) for (const f of action.input.fields) init[f.name] = f.type === 'password' ? '' : row?.[f.name] ?? f.default ?? (f.type === 'multiselect' ? [] : '');
    if (action.input && 'type' in action.input && action.input.type === 'json') init.payload = JSON.stringify(row?.[action.input.field] ?? {}, null, 2);
    setValues(init);
    if (!action.input && !action.confirm) return submit(action, row, {});
    setOpen({ action, row });
  };

  const submit = async (action: ActionDef, row: Record<string, any> | null, input: Record<string, any>) => {
    const fields = action.input && 'fields' in action.input ? action.input.fields : action.input ? [{ name: 'payload', label: 'JSON', type: 'json' as const, required: true }] : [];
    const problem = validateFields(fields, input);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      const res = await ctx.action(action.id, row?.id ?? null, action.input && 'fields' in action.input ? visibleValues(action.input.fields, input) : input);
      toast(res.message ?? `${action.label}: done`, 'success');
      setOpen(null);
      ctx.refetch();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const tableActions = actions.filter((a) => a.scope === 'table');
  const rowActions = actions.filter((a) => a.scope !== 'table');
  const cols = resolveColumns(data, props);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {tableActions.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          {tableActions.map((a) => (
            <button key={a.id} className={`btn sm${a.icon ? '' : ' primary'}`} onClick={() => start(a, null)}>
              <Icon name={a.icon ?? 'plus'} size={13} /> {a.label}
            </button>
          ))}
        </div>
      )}
      <Table
        columns={cols}
        rows={data?.rows ?? []}
        pageSize={props.pageSize ?? 0}
        caption={widget.title}
        emptyText={props.emptyText}
        rowActions={
          rowActions.length
            ? (row) => {
                const list = rowActions.filter((a) => applies(a, row));
                const pinned = list.filter((a) => a.inline);
                const others = list.filter((a) => !a.inline);
                // With pinned buttons (e.g. ↑ ↓) everything else goes in the menu to keep rows narrow.
                const inline = pinned.length ? [] : others.length > 2 ? others.slice(0, 1) : others;
                const rest = others.filter((a) => !inline.includes(a));
                return (
                  <span className="row-actions">
                    {pinned.map((a) =>
                      a.icon ? (
                        <button key={`${a.id}:${a.label}`} className="icon-btn" aria-label={a.label} title={a.label} onClick={() => start(a, row)}>
                          <Icon name={a.icon} size={14} />
                        </button>
                      ) : (
                        <button key={`${a.id}:${a.label}`} className="btn sm ghost" onClick={() => start(a, row)}>
                          {a.label}
                        </button>
                      ),
                    )}
                    {inline.map((a) => (
                      <button key={`${a.id}:${a.label}`} className={`btn sm ghost${a.danger ? ' danger' : ''}`} onClick={() => start(a, row)}>
                        {a.label}
                      </button>
                    ))}
                    {rest.length > 0 && <RowMenu actions={rest} onPick={(a) => start(a, row)} />}
                  </span>
                );
              }
            : undefined
        }
      />
      {open && (
        <Modal
          title={`${open.action.label}${open.row ? ` — ${open.row.displayName ?? open.row.title ?? open.row.name ?? open.row.domain ?? open.row.storeName ?? open.row.id}${open.row.key ? `/${open.row.key}` : ''}` : ''}`}
          onClose={() => setOpen(null)}
          wide={!!open.action.input && 'type' in open.action.input}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(null)}>
                Cancel
              </button>
              <button className="btn primary" disabled={busy} onClick={() => submit(open.action, open.row, values)}>
                {busy ? 'Working…' : open.action.input && 'type' in open.action.input ? 'Save & publish' : open.action.input ? 'Save' : 'Confirm'}
              </button>
            </>
          }
        >
          {open.action.confirm && <p style={{ margin: 0 }}>{open.action.confirm}</p>}
          {open.action.input && 'fields' in open.action.input &&
            visibleFields(open.action.input.fields, values).map((f) => <Field key={f.name} f={f} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} options={data?.options} />)}
          {open.action.input && 'type' in open.action.input && (
            <>
              <Field f={{ name: 'payload', label: 'Configuration (JSON)', type: 'json' }} value={values.payload} onChange={(v) => setValues((s) => ({ ...s, payload: v }))} />
              <div className="hint">Validated through 8 layers (schema → reference → data source → metric → dependency → permission → tenant → quota) before it is published as a new version.</div>
            </>
          )}
          {error && <div className="error-text" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
        </Modal>
      )}
      {history && (
        <Modal title={`Version history — ${history.row?.id}`} onClose={() => setHistory(null)} wide>
          <table className="data">
            <thead>
              <tr>
                <th>Version</th>
                <th>Published</th>
                <th>By</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.items.map((h) => (
                <tr key={h.version}>
                  <td>v{h.version} {h.isCurrent && <span className="badge good">current</span>}</td>
                  <td>{formatValue(h.publishedAt, 'datetime')}</td>
                  <td>{h.publishedBy}</td>
                  <td>{h.note}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!h.isCurrent && actions.some((a) => a.id === 'rollback') && (
                      <button
                        className="btn sm"
                        onClick={async () => {
                          try {
                            const res = await ctx.action('rollback', history.row.id, { version: h.version });
                            toast(res.message, 'success');
                            setHistory(null);
                            ctx.refetch();
                          } catch (e) {
                            toast(errorMessage(e), 'error');
                          }
                        }}
                      >
                        Roll back to v{h.version}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}

export const crudTable: RegistryEntry = { component: CrudTable, scroll: true };
